import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Beaker, Gauge, Timer, Weight, CheckCircle2, AlertCircle,
  Info, ChevronRight, Save, RotateCcw, Microscope,
  TrendingUp, BarChart3, Zap, Thermometer, CircleDot,
  Factory, Milk, FishSymbol, ShieldAlert, Droplets,
  ArrowDown, ArrowRightLeft, FlaskConical,
  Settings, Layers, Database
} from 'lucide-react';
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

/* ─── pH Adjustment Tank Specification ─────────────────
   Acid/base neutralization with retention, mixing,
   CO₂/NaOH/HCl dosing, and effluent pH target
   ──────────────────────────────────────────────────── */

interface PHParams {
  flowRate: number;        // m³/day
  influentpH: number;      // pH in
  targetpH: number;        // pH out
  influentAlkalinity: number; // mg/L as CaCO₃
  influentTSS: number;    // mg/L
  influentCOD: number;     // mg/L
  og: number;
  ph: number;
  alkalinity: number;
  turbidity: number;
  conductivity: number;
  hardness: number;
  nh3n: number;
  tp: number;
  // Tank
  tankDiameter: number;    // m
  tankDepth: number;       // m
  freeboard: number;       // m
  // Neutralization
  reagentType: 'naoh' | 'hcl' | 'co2' | 'sulfuric' | 'lime';
  reagentConcentration: number; // % w/w
  reagentDose: number;     // mg/L (user adjustable)
  reagentDensity: number;   // kg/L
  mixerPower: number;      // kW
  mixerRPM: number;       // rpm
  mixerDiameter: number;    // m (impeller)
  // Process
  temperature: number;     // °C
  safetyFactor: number;     // 1.0-2.0
}

interface PHSize {
  tankArea: number;        // m²
  tankVolume: number;      // m³ working
  totalVolume: number;     // m³ incl freeboard
  actualRetention: number; // min
  superficialVelocity: number; // m/h
  // Chemistry
  pHChange: number;        // delta pH
  acidBaseDemand: number;  // mg/L as CaCO₃ equiv
  reagentDemand: number;   // mg/L reagent required
  reagentFlow: number;     // L/day
  reagentPumpRate: number; // mL/min
  dailyConsumption: number; // kg/day
  reagentVolume: number;   // L/day solution
  // Alkalinity check
  alkalinityRequired: number; // mg/L CaCO₃
  alkalinityShortfall: number; // mg/L
  // Mixer
  tipSpeed: number;       // m/s
  velocityGradient: number; // s⁻¹
  mixerPowerPerVolume: number; // kW/m³
  turnoverTime: number;    // min
  // Neutralization completeness
  reactionCompleteness: number; // 0-1
  actualEffluentPH: number;
  phDeviation: number;
  withinCompliance: boolean;
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'overloaded';
  statusDetail: string;
  // Risk
  overDoseRisk: 'low' | 'moderate' | 'high';
  underDoseRisk: 'low' | 'moderate' | 'high';
}

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

const PROFILES = [
  { name: 'Dairy Effluent', description: 'Acidic pH 6.2. NaOH to pH 7.0. High buffering.', icon: Milk,
    defaults: { flowRate: 350, influentpH: 6.2, targetpH: 7.0, influentAlkalinity: 800, influentTSS: 1200, influentCOD: 3500,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      tankDiameter: 2.5, tankDepth: 2.5, freeboard: 0.5,
      reagentType: 'naoh', reagentConcentration: 30, reagentDose: 180, reagentDensity: 1.33,
      mixerPower: 1.5, mixerRPM: 60, mixerDiameter: 0.6, temperature: 25, safetyFactor: 1.3 } },
  { name: 'Brewery Effluent', description: 'Acidic pH 5.8. NaOH to pH 7.2. Low alkalinity.', icon: Factory,
    defaults: { flowRate: 500, influentpH: 5.8, targetpH: 7.2, influentAlkalinity: 300, influentTSS: 2500, influentCOD: 6000,
      og: 180, ph: 5.2, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      tankDiameter: 3.0, tankDepth: 2.5, freeboard: 0.5,
      reagentType: 'naoh', reagentConcentration: 30, reagentDose: 350, reagentDensity: 1.33,
      mixerPower: 2.2, mixerRPM: 60, mixerDiameter: 0.7, temperature: 20, safetyFactor: 1.4 } },
  { name: 'Fish Processing', description: 'Neutral pH 6.5. Minor adjustment to 7.0.', icon: FishSymbol,
    defaults: { flowRate: 250, influentpH: 6.5, targetpH: 7.0, influentAlkalinity: 500, influentTSS: 1800, influentCOD: 2800,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      tankDiameter: 2.0, tankDepth: 2.5, freeboard: 0.5,
      reagentType: 'naoh', reagentConcentration: 20, reagentDose: 120, reagentDensity: 1.20,
      mixerPower: 1.1, mixerRPM: 45, mixerDiameter: 0.5, temperature: 15, safetyFactor: 1.2 } },
];

export function calcPH(p: PHParams): PHSize {
  const q = p.flowRate / 24; // m³/h

  // ─── Water properties ───
  const tempK = p.temperature + 273.15;
  const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140); // Pa·s
  const rhoWater = 998 - 0.2 * (p.temperature - 20); // kg/m³

  // ─── Tank geometry ───
  const area = Math.PI * Math.pow(p.tankDiameter / 2, 2);
  const volume = area * p.tankDepth;
  const totalVolume = area * (p.tankDepth + p.freeboard);

  // ─── Retention time ───
  const detentionTime = volume / q; // h
  const detentionMin = detentionTime * 60; // min

  // ─── Neutralization chemistry ───
  const phDiff = Math.abs(p.targetpH - p.influentpH);
  const isAcidic = p.influentpH < p.targetpH;

  // Buffer capacity estimate: 1 mmol/L alkalinity ≈ 50 mg/L CaCO3 per pH unit
  const bufferCapacity = p.influentAlkalinity / 50; // mg/L CaCO3 per pH unit (empirical)
  const chemicalDemand = phDiff * bufferCapacity * p.safetyFactor; // mg/L as CaCO3

  // ─── Chemical selection and dosing ───
  // NaOH: 1 mg/L neutralizes 1.25 mg/L acidity as CaCO3 (MW ratio)
  // HCl: 1 mg/L neutralizes 1.37 mg/L alkalinity as CaCO3
  // CO2: 1 mg/L neutralizes 0.91 mg/L alkalinity as CaCO3
  // Ca(OH)2: 1 mg/L neutralizes 1.35 mg/L acidity as CaCO3
  const chemFactor = p.reagentType === 'naoh' ? 1.25 :
                     p.reagentType === 'hcl' ? 1.37 :
                     p.reagentType === 'co2' ? 0.91 :
                     p.reagentType === 'sulfuric' ? 1.37 :
                     p.reagentType === 'lime' ? 1.35 : 1.0;

  const chemicalDose = chemicalDemand / chemFactor; // mg/L of selected chemical
  const dailyChemical = (p.flowRate * chemicalDose) / 1000; // kg/day
  const chemicalVolume = dailyChemical / (p.reagentConcentration * 10); // m³/day (solution volume)

  // ─── CO2 system (if selected) ───
  // CO2 + H2O ↔ H2CO3 ↔ H+ + HCO3- (pKa1 = 6.35 at 25°C)
  const pKa1 = 6.35 - 0.005 * (p.temperature - 25); // temperature correction
  const co2Required = p.reagentType === 'co2' ? dailyChemical : 0; // kg/day

  // ─── Neutralization reaction completeness ───
  // With good mixing and >15 min detention, >95% complete
  // With poor mixing or short detention, lower completeness
  const mixingEfficiency = p.mixerPower > 1.5 ? 0.95 :
                           p.mixerPower > 0.8 ? 0.90 :
                           p.mixerPower > 0.3 ? 0.85 : 0.70;
  const detentionEfficiency = detentionMin >= 20 ? 0.98 : detentionMin >= 10 ? 0.93 : detentionMin >= 5 ? 0.85 : 0.70;
  const reactionCompleteness = mixingEfficiency * detentionEfficiency;
  const actualEffluentPH = p.influentpH + (p.targetpH - p.influentpH) * reactionCompleteness;

  // ─── Safety margin ───
  const phTolerance = 0.5;
  const phDeviation = Math.abs(actualEffluentPH - p.targetpH);
  const withinCompliance = phDeviation <= phTolerance;

  // ─── Power (mixer) ───
  const mixerPowerW = p.mixerPower * 1000;
  const gValue = p.mixerPower > 0 ? Math.sqrt(mixerPowerW / (mu * volume)) : 0;
  const tipSpeed = p.mixerRPM > 0 ? (Math.PI * p.mixerDiameter * p.mixerRPM) / 60 : 0;
  const mixerPowerPerVolume = volume > 0 ? p.mixerPower / volume : 0;

  // ─── Status ───
  let status: PHSize['status']; let statusDetail: string;
  if (detentionMin >= 15 && reactionCompleteness >= 0.90 && withinCompliance && gValue >= 20) {
    status = 'optimal'; statusDetail = `Optimal. Detention ${detentionMin.toFixed(0)} min, completeness ${(reactionCompleteness*100).toFixed(0)}%, effluent pH ${actualEffluentPH.toFixed(1)}.`;
  } else if (detentionMin >= 8 && reactionCompleteness >= 0.80 && phDeviation <= 1.0) {
    status = 'acceptable'; statusDetail = `Acceptable. Detention ${detentionMin.toFixed(0)} min. Monitor effluent pH.`;
  } else if (detentionMin < 3 || reactionCompleteness < 0.60 || phDeviation > 2) {
    status = 'overloaded'; statusDetail = `Overloaded. Detention ${detentionMin.toFixed(0)} min or reaction ${(reactionCompleteness*100).toFixed(0)}% too low. Increase volume or mixing.`;
  } else if (volume < 1 || area < 0.5) {
    status = 'undersized'; statusDetail = `Undersized. Volume ${volume.toFixed(1)} m³. Increase tank size.`;
  } else {
    status = 'oversized'; statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  return {
    tankArea: area, tankVolume: volume, totalVolume,
    actualRetention: detentionMin, superficialVelocity: q / area,
    pHChange: phDiff, acidBaseDemand: chemicalDemand,
    reagentDemand: chemicalDose, reagentFlow: chemicalVolume * 1000,
    reagentPumpRate: (chemicalVolume * 1000 * 1000) / (24 * 60),
    dailyConsumption: dailyChemical,
    reagentVolume: chemicalVolume * 1000,
    alkalinityRequired: chemicalDemand, alkalinityShortfall: Math.max(0, chemicalDemand - p.influentAlkalinity),
    tipSpeed, velocityGradient: gValue, mixerPowerPerVolume,
    turnoverTime: detentionMin,
    reactionCompleteness, actualEffluentPH, phDeviation, withinCompliance,
    overDoseRisk: p.reagentDose > chemicalDose * 2 ? 'high' : p.reagentDose > chemicalDose * 1.3 ? 'moderate' : 'low',
    underDoseRisk: p.reagentDose < chemicalDose * 0.5 ? 'high' : p.reagentDose < chemicalDose * 0.8 ? 'moderate' : 'low',
    status, statusDetail,
  };
}

/* ─── UI Components ──────────────────────────────────── */
function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && <div className="group relative"><Info className="w-3 h-3 text-slate-600 cursor-help" /><div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">{info}</div></div>}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">{step < 0.1 ? value.toFixed(2) : step < 1 ? value.toFixed(1) : value.toFixed(0)}<span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span></span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer" style={{ accentColor: color }} />
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]"><span className="text-slate-500">{label}</span><span className="font-bold" style={{ color }}>{typeof value === 'number' ? value.toFixed(1) : value} {unit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} /></div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button onClick={onClick} className={`w-full text-left p-3 rounded-xl border transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-500/20' : 'bg-slate-800'}`}><Icon className={`w-4 h-4 ${isActive ? 'text-amber-400' : 'text-slate-500'}`} /></div>
        <div><p className={`text-xs font-semibold ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p><p className="text-[10px] text-slate-500 leading-tight">{profile.description}</p></div>
      </div>
    </button>
  );
}

function AB({ level, title, message }: any) {
  const s: any = { low: { b: 'border-emerald-500/20', bg: 'bg-emerald-500/5', c: 'text-emerald-400', i: CheckCircle2 }, moderate: { b: 'border-amber-500/20', bg: 'bg-amber-500/5', c: 'text-amber-400', i: AlertCircle }, high: { b: 'border-orange-500/20', bg: 'bg-orange-500/5', c: 'text-orange-400', i: ShieldAlert }, critical: { b: 'border-rose-500/20', bg: 'bg-rose-500/5', c: 'text-rose-400', i: ShieldAlert } };
  const Icon = s[level].i;
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`rounded-xl border p-3 ${s[level].bg} ${s[level].b}`}>
      <div className="flex items-center gap-2"><Icon className={`w-4 h-4 ${s[level].c}`} /><span className={`text-xs font-bold uppercase ${s[level].c}`}>{title}</span></div>
      <p className={`text-[10px] mt-1 leading-relaxed ${s[level].c} opacity-80`}>{message}</p>
    </motion.div>
  );
}

/* ─── pH Scale Visual ────────────────────────────────── */
function PHScale({ influent, target }: { influent: number; target: number }) {
  const scaleWidth = 100;
  const influentPos = ((influent - 2) / 10) * scaleWidth;
  const targetPos = ((target - 2) / 10) * scaleWidth;
  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-2 text-center">pH Scale</p>
      <svg viewBox="0 0 120 30" className="w-full h-10">
        <defs>
          <linearGradient id="phGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f43f5e" /><stop offset="25%" stopColor="#f59e0b" /><stop offset="50%" stopColor="#10b981" />
            <stop offset="75%" stopColor="#38bdf8" /><stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
        <rect x="10" y="10" width="100" height="6" rx="3" fill="url(#phGradient)" />
        <text x="10" y="26" fill="#f43f5e" fontSize="8" textAnchor="middle">2</text>
        <text x="35" y="26" fill="#f59e0b" fontSize="8" textAnchor="middle">4</text>
        <text x="60" y="26" fill="#10b981" fontSize="8" textAnchor="middle">7</text>
        <text x="85" y="26" fill="#38bdf8" fontSize="8" textAnchor="middle">10</text>
        <text x="110" y="26" fill="#8b5cf6" fontSize="8" textAnchor="middle">12</text>
        <line x1={10 + influentPos} y1="6" x2={10 + influentPos} y2="20" stroke="#f59e0b" strokeWidth="2" />
        <text x={10 + influentPos} y="4" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">In {influent}</text>
        <line x1={10 + targetPos} y1="6" x2={10 + targetPos} y2="20" stroke="#10b981" strokeWidth="2" />
        <text x={10 + targetPos} y="28" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Target {target}</text>
      </svg>
    </div>
  );
}

/* ─── Enterprise Animated Flow Diagram ────────────────── */
function FlowDiagram({ params, size }: { params: PHParams; size: PHSize }) {
  const safe = {
    reagentDemand: typeof size?.reagentDemand === 'number' ? size.reagentDemand : 0,
    reagentFlow: typeof size?.reagentFlow === 'number' ? size.reagentFlow : 0,
    reagentVolume: typeof size?.reagentVolume === 'number' ? size.reagentVolume : 0,
    reagentPumpRate: typeof size?.reagentPumpRate === 'number' ? size.reagentPumpRate : 0,
    actualRetention: typeof size?.actualRetention === 'number' ? size.actualRetention : 0,
    tipSpeed: typeof size?.tipSpeed === 'number' ? size.tipSpeed : 0,
    mixerPowerPerVolume: typeof size?.mixerPowerPerVolume === 'number' ? size.mixerPowerPerVolume : 0,
    gValue: typeof size?.gValue === 'number' ? size.gValue : 0,
    power: typeof size?.mixerPowerPerVolume === 'number' ? size.mixerPowerPerVolume : 0,
    tankVolume: typeof size?.tankVolume === 'number' ? size.tankVolume : 0,
    totalVolume: typeof size?.totalVolume === 'number' ? size.totalVolume : 0,
  };

  const reagentName = params.reagentType === 'naoh' ? 'NaOH' : params.reagentType === 'hcl' ? 'HCl' : params.reagentType === 'co2' ? 'CO₂' : params.reagentType === 'sulfuric' ? 'H₂SO₄' : 'Lime';

  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowDown className="w-3 h-3" /> Process Flow — Influent → pH Tank → Neutralization → Effluent
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs>
          <linearGradient id="waterGradPH" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">pH ADJUSTMENT — NEUTRALIZATION TRAIN</text>

        {/* Influent */}
        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradPH)" stroke="#f59e0b" strokeWidth="2" />
        <rect x="34" y="59" width="92" height="20" rx="3" fill="#f59e0b" opacity="0.15" />
        <text x="80" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Influent</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 0)} m³/day</text>
        <text x="80" y="100" fill="#f59e0b" fontSize="8" textAnchor="middle">pH {fmt(params.influentpH, 1)}</text>
        <text x="80" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">Alk {fmt(params.influentAlkalinity, 0)} mg/L</text>
        <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">TSS {fmt(params.influentTSS, 0)} mg/L</text>

        <line x1="132" y1="95" x2="170" y2="95" stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="168,92 175,95 168,98" fill="#f59e0b" />

        {/* Dosing Pump */}
        <g transform="translate(172, 78)">
          <circle cx="18" cy="14" r="14" fill="#0f172a" stroke="#06b6d4" strokeWidth="1.5" />
          <line x1="18" y1="6" x2="18" y2="22" stroke="#06b6d4" strokeWidth="1.5" />
          <line x1="10" y1="14" x2="26" y2="14" stroke="#06b6d4" strokeWidth="1.5" />
          <rect x="-6" y="10" width="12" height="8" rx="2" fill="var(--rb-active)" stroke="#06b6d4" strokeWidth="1" />
          <text x="18" y="38" fill="#06b6d4" fontSize="7" textAnchor="middle">Dosing Pump</text>
        </g>

        <line x1="190" y1="95" x2="220" y2="95" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4 3" />
        <polygon points="218,92 225,95 218,98" fill="#ef4444" />

        {/* pH TANK */}
        <rect x="225" y="40" width="140" height="110" rx="6" fill="url(#waterGradPH)" stroke="#ef4444" strokeWidth="2" />
        <rect x="229" y="44" width="132" height="20" rx="3" fill="#ef4444" opacity="0.15" />
        <text x="295" y="57" fill="#ef4444" fontSize="9" textAnchor="middle" fontWeight="bold">pH Adjustment Tank</text>
        <text x="295" y="75" fill="#94a3b8" fontSize="8" textAnchor="middle">Ø{fmt(params.tankDiameter, 1)}m × {fmt(params.tankDepth, 1)}m</text>
        <text x="295" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Vol {fmt(safe.tankVolume, 1)} m³ | Retention {fmt(safe.actualRetention, 0)} min</text>
        <text x="295" y="101" fill="#f59e0b" fontSize="8" textAnchor="middle">Mixer {fmt(params.mixerPower, 1)} kW @ {params.mixerRPM} rpm</text>
        <text x="295" y="114" fill="#f59e0b" fontSize="8" textAnchor="middle">G-value {fmt(safe.gValue, 0)} s⁻¹ | Tip speed {fmt(safe.tipSpeed, 1)} m/s</text>
        <text x="295" y="127" fill="#94a3b8" fontSize="8" textAnchor="middle">Camp-Stein: G = √(P/μV)</text>

        <motion.g animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 3, ease: "linear" }} style={{ transformOrigin: '295px 80px' }}>
          <line x1="295" y1="68" x2="295" y2="92" stroke="#ef4444" strokeWidth="1.5" />
          <line x1="283" y1="80" x2="307" y2="80" stroke="#ef4444" strokeWidth="1.5" />
        </motion.g>

        <text x="215" y="160" fill="#ef4444" fontSize="7" textAnchor="middle">{reagentName} {fmt(params.reagentDose, 0)} mg/L @ {params.reagentConcentration}%</text>
        <text x="215" y="170" fill="#94a3b8" fontSize="7" textAnchor="middle">Demand {fmt(safe.reagentDemand, 1)} kg/day</text>

        <line x1="367" y1="95" x2="410" y2="95" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="408,92 415,95 408,98" fill="#10b981" />

        {/* pH Monitor */}
        <g transform="translate(412, 70)">
          <rect x="0" y="0" width="50" height="50" rx="4" fill="#0f172a" stroke="#10b981" strokeWidth="1.5" />
          <text x="25" y="15" fill="#10b981" fontSize="7" textAnchor="middle">pH Probe</text>
          <text x="25" y="30" fill="#10b981" fontSize="12" textAnchor="middle" fontWeight="bold">{fmt(params.targetpH, 1)}</text>
          <text x="25" y="42" fill="#94a3b8" fontSize="7" textAnchor="middle">Setpoint</text>
        </g>

        <line x1="464" y1="95" x2="500" y2="95" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="498,92 505,95 498,98" fill="#10b981" />

        {/* Effluent */}
        <rect x="507" y="55" width="100" height="80" rx="6" fill="url(#waterGradPH)" stroke="#10b981" strokeWidth="2" />
        <rect x="511" y="59" width="92" height="20" rx="3" fill="#10b981" opacity="0.15" />
        <text x="557" y="72" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Effluent</text>
        <text x="557" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">pH {fmt(params.targetpH, 1)}</text>
        <text x="557" y="100" fill="#94a3b8" fontSize="8" textAnchor="middle">Retention {fmt(safe.actualRetention, 0)} min</text>
        <text x="557" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">Power {fmt(safe.mixerPowerPerVolume, 2)} kW/m³</text>
        <text x="557" y="124" fill="#f59e0b" fontSize="8" textAnchor="middle">{reagentName} {fmt(safe.reagentDemand, 1)} kg/day</text>

        {/* Chemistry note */}
        <text x="295" y="182" fill="#94a3b8" fontSize="7" textAnchor="middle">Chemistry: {params.reagentType === 'naoh' ? 'NaOH + H⁺ → Na⁺ + H₂O' : params.reagentType === 'hcl' ? 'HCl + OH⁻ → Cl⁻ + H₂O' : params.reagentType === 'co2' ? 'CO₂ + H₂O → H₂CO₃ → H⁺ + HCO₃⁻' : params.reagentType === 'sulfuric' ? 'H₂SO₄ + 2NaOH → Na₂SO₄ + 2H₂O' : 'Ca(OH)₂ + CO₂ → CaCO₃ + H₂O'}</text>

        {/* Legend */}
        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#f59e0b" fontSize="9">— Acidic Influent</text>
        <text x="145" y="359" fill="#ef4444" fontSize="9">— Reagent Dosing</text>
        <text x="245" y="359" fill="#10b981" fontSize="9">— Neutralized Effluent</text>
        <text x="345" y="359" fill="#06b6d4" fontSize="9">— Dosing Pump</text>
        <text x="445" y="359" fill="#94a3b8" fontSize="9">G = √(P/μV) | N = Q×ΔpH×Alk×SF | τ = V/Q</text>
      </svg>
    </div>
  );
}

export default function PHSpecPage() {
  const [params, setParams] = useState<PHParams>({
    flowRate: 350, influentpH: 6.2, targetpH: 7.0, influentAlkalinity: 800, influentTSS: 1200, influentCOD: 3500,
    og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
    tankDiameter: 2.5, tankDepth: 2.5, freeboard: 0.5,
    reagentType: 'naoh', reagentConcentration: 30, reagentDose: 180, reagentDensity: 1.33,
    mixerPower: 1.5, mixerRPM: 60, mixerDiameter: 0.6, temperature: 25, safetyFactor: 1.3,
  });

  const [activeProfile, setActiveProfile] = useState(0);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'influent' | 'tank' | 'chemistry'>('influent');

  const size = useMemo(() => calcPH(params), [params]);
  const sampleLoader = useSampleLoader<PHParams>(setParams);

  const update = (key: keyof PHParams, val: number | string) => { setParams(p => ({ ...p, [key]: val })); setSaved(false); };
  const loadProfile = (idx: number) => { const prof = PROFILES[idx]; if (prof.defaults) { setParams(p => ({ ...p, ...(prof.defaults as Partial<PHParams>) })); setActiveProfile(idx); setSaved(false); toast.success(`Loaded ${prof.name} profile`); } };
  const handleSave = () => { localStorage.setItem('rb_ph_spec', JSON.stringify(params)); setSaved(true); toast.success('pH specification saved'); };
  const handleReset = () => { loadProfile(0); setSaved(false); sampleLoader.clearSample(); };

  useEffect(() => { const s = localStorage.getItem('rb_ph_spec'); if (s) { try { setParams(JSON.parse(s)); } catch {} } }, []);

  // Science accordion sections
  const scienceSections = useMemo(() => {
    const q = params.flowRate / 24;
    const tempK = params.temperature + 273.15;
    const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140);
    const rhoWater = 998 - 0.2 * (params.temperature - 20);
    const area = Math.PI * Math.pow(params.tankDiameter / 2, 2);
    const volume = area * params.tankDepth;
    const detentionTime = volume / q;
    const detentionMin = detentionTime * 60;
    const phDiff = Math.abs(params.targetpH - params.influentpH);
    const bufferCapacity = params.influentAlkalinity / 50;
    const chemicalDemand = phDiff * bufferCapacity * params.safetyFactor;
    const chemFactor = params.reagentType === 'naoh' ? 1.25 :
                       params.reagentType === 'hcl' ? 1.37 :
                       params.reagentType === 'co2' ? 0.91 :
                       params.reagentType === 'sulfuric' ? 1.37 :
                       params.reagentType === 'lime' ? 1.35 : 1.0;
    const chemicalDose = chemicalDemand / chemFactor;
    const dailyChemical = (params.flowRate * chemicalDose) / 1000;
    const mixingEfficiency = params.mixerPower > 1.5 ? 0.95 :
                             params.mixerPower > 0.8 ? 0.90 :
                             params.mixerPower > 0.3 ? 0.85 : 0.70;
    const detentionEfficiency = detentionMin >= 20 ? 0.98 : detentionMin >= 10 ? 0.93 : detentionMin >= 5 ? 0.85 : 0.70;
    const reactionCompleteness = mixingEfficiency * detentionEfficiency;
    const actualEffluentPH = params.influentpH + (params.targetpH - params.influentpH) * reactionCompleteness;
    const phDeviation = Math.abs(actualEffluentPH - params.targetpH);
    const withinCompliance = phDeviation <= 0.5;
    const mixerPowerW = params.mixerPower * 1000;
    const gValue = params.mixerPower > 0 ? Math.sqrt(mixerPowerW / (mu * volume)) : 0;
    const tipSpeed = params.mixerRPM > 0 ? (Math.PI * params.mixerDiameter * params.mixerRPM) / 60 : 0;
    const mixerPowerPerVolume = volume > 0 ? params.mixerPower / volume : 0;
    return [
      {
        title: 'Tank Physics & Retention',
        icon: Beaker,
        equations: [
          { label: 'Flow rate (hourly)', formula: 'q = flowRate / 24', substitution: `q = ${sharedFmt(params.flowRate, 0)} / 24 = ${sharedFmt(q, 1)} m³/h`, result: sharedFmt(q, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Tank area', formula: 'A = π×(D/2)²', substitution: `A = π×(${params.tankDiameter}/2)² = ${sharedFmt(area, 2)} m²`, result: sharedFmt(area, 2), status: 'pass', unit: 'm²' },
          { label: 'Working volume', formula: 'V = A × depth', substitution: `V = ${sharedFmt(area, 2)} × ${params.tankDepth} = ${sharedFmt(volume, 1)} m³`, result: sharedFmt(volume, 1), status: 'pass', unit: 'm³' },
          { label: 'Retention time', formula: 'τ = V/q × 60', substitution: `τ = ${sharedFmt(volume, 1)}/${sharedFmt(q, 1)} × 60 = ${sharedFmt(detentionMin, 0)} min`, result: sharedFmt(detentionMin, 0), status: detentionMin >= 15 ? 'pass' : detentionMin >= 8 ? 'warn' : 'fail', unit: 'min' },
          { label: 'Water viscosity', formula: 'μ = 0.001 × 10^(247.8/T_K − 140)', substitution: `μ = 0.001 × 10^(247.8/${sharedFmt(tempK, 1)} − 140) = ${sharedFmt(mu * 1000, 3)} mPa·s`, result: sharedFmt(mu * 1000, 3), status: 'pass', unit: 'mPa·s' },
        ],
      },
      {
        title: 'Neutralization Chemistry',
        icon: FlaskConical,
        equations: [
          { label: 'pH change', formula: 'ΔpH = |target − influent|', substitution: `ΔpH = |${params.targetpH} − ${sharedFmt(params.influentpH, 1)}| = ${sharedFmt(phDiff, 1)}`, result: sharedFmt(phDiff, 1), status: 'pass', unit: '' },
          { label: 'Buffer capacity', formula: 'BC = Alkalinity / 50', substitution: `BC = ${params.influentAlkalinity} / 50 = ${sharedFmt(bufferCapacity, 1)}`, result: sharedFmt(bufferCapacity, 1), status: 'pass', unit: '' },
          { label: 'Chemical demand', formula: 'N = ΔpH × BC × SF', substitution: `N = ${sharedFmt(phDiff, 1)} × ${sharedFmt(bufferCapacity, 1)} × ${params.safetyFactor} = ${sharedFmt(chemicalDemand, 0)} mg/L CaCO₃`, result: sharedFmt(chemicalDemand, 0), status: 'pass', unit: 'mg/L' },
          { label: 'Reagent factor', formula: 'f = f(reagentType)', substitution: `f = ${params.reagentType} → ${chemFactor}`, result: String(chemFactor), status: 'pass', unit: '' },
          { label: 'Reagent dose', formula: 'Dose = N / f', substitution: `Dose = ${sharedFmt(chemicalDemand, 0)} / ${chemFactor} = ${sharedFmt(chemicalDose, 1)} mg/L`, result: sharedFmt(chemicalDose, 1), status: 'pass', unit: 'mg/L' },
          { label: 'Daily consumption', formula: 'kg/day = flowRate × Dose / 1000', substitution: `${sharedFmt(params.flowRate, 0)} × ${sharedFmt(chemicalDose, 1)} / 1000 = ${sharedFmt(dailyChemical, 1)} kg/day`, result: sharedFmt(dailyChemical, 1), status: 'pass', unit: 'kg/day' },
          { label: 'Reaction completeness', formula: 'η = η_mix × η_det', substitution: `η = ${sharedFmt(mixingEfficiency, 2)} × ${sharedFmt(detentionEfficiency, 2)} = ${sharedFmt(reactionCompleteness, 2)}`, result: sharedFmt(reactionCompleteness, 2), status: reactionCompleteness >= 0.90 ? 'pass' : reactionCompleteness >= 0.80 ? 'warn' : 'fail', unit: '' },
          { label: 'Estimated effluent pH', formula: 'pH_out = pH_in + ΔpH × η', substitution: `pH_out = ${sharedFmt(params.influentpH, 1)} + ${sharedFmt(phDiff, 1)} × ${sharedFmt(reactionCompleteness, 2)} = ${sharedFmt(actualEffluentPH, 1)}`, result: sharedFmt(actualEffluentPH, 1), status: withinCompliance ? 'pass' : 'warn', unit: '' },
        ],
      },
      {
        title: 'Mixer — Camp-Stein',
        icon: Zap,
        equations: [
          { label: 'G-value', formula: 'G = √(P/μV)', substitution: `G = √(${mixerPowerW} / (${sharedFmt(mu, 5)} × ${sharedFmt(volume, 1)})) = ${sharedFmt(gValue, 0)} s⁻¹`, result: sharedFmt(gValue, 0), status: gValue >= 20 ? 'pass' : gValue >= 10 ? 'warn' : 'fail', unit: 's⁻¹' },
          { label: 'Tip speed', formula: 'v = π×D×N/60', substitution: `v = π×${params.mixerDiameter}×${params.mixerRPM}/60 = ${sharedFmt(tipSpeed, 1)} m/s`, result: sharedFmt(tipSpeed, 1), status: 'pass', unit: 'm/s' },
          { label: 'Power per volume', formula: 'P/V', substitution: `${params.mixerPower} / ${sharedFmt(volume, 1)} = ${sharedFmt(mixerPowerPerVolume, 3)} kW/m³`, result: sharedFmt(mixerPowerPerVolume, 3), status: 'pass', unit: 'kW/m³' },
        ],
      },
    ];
  }, [params, size]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    overloaded: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    undersized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: AlertCircle },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };

  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Beaker}
        iconColor="text-sky-400"
        title="pH Adjustment Tank Specification"
        subtitle="Neutralization chemistry — Buffer capacity, Camp-Stein mixing, reagent dosing"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
            <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
            <button onClick={() => navigate('/equipment-validation?tab=ph')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-violet-500/10 text-violet-400 text-xs font-bold border border-violet-500/20 hover:bg-violet-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
          </div>
        }
      />

      {/* Sample Selector */}
      <SampleSelector loader={sampleLoader} />

      {/* Loaded sample readout */}
      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'TSS', value: `${sharedFmt(params.influentTSS, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tss || 0, 0)}` },
              { label: 'COD', value: `${sharedFmt(params.influentCOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.cod || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* LEFT: Flow Panel */}
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between px-4 py-2 rb-inner-card">
            <h3 className="text-xs font-semibold text-cyan-400 flex items-center gap-2">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Process Flow Diagram
            </h3>
            <button onClick={() => setShowFlow(!showFlow)} className="text-[10px] px-2 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all">
              {showFlow ? 'Collapse' : 'Expand'}
            </button>
          </div>
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
              Reynolds &amp; Bauhm Smart Tool 2.0 — All Rights Reserved
            </p>
          </div>
          {showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}
          <PHScale influent={params.influentpH} target={params.targetpH} />
        </div>

        {/* RIGHT: Content Panel */}
        <div className="xl:col-span-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PROFILES.map((prof, i) => (
              <PC key={prof.name} profile={prof} isActive={activeProfile === i} onClick={() => loadProfile(i)} />
            ))}
          </div>

          <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${sc.text}`} />
              <span className={`text-xs font-bold uppercase ${sc.text}`}>{size.status}</span>
            </div>
            <p className={`text-[10px] mt-1 leading-relaxed ${sc.text} opacity-80`}>{size.statusDetail}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <AB level={size.overDoseRisk} title="Over-dose Risk" message={size.overDoseRisk === 'high' ? 'Reagent dose significantly exceeds demand. Waste and cost concern.' : 'Dose within reasonable range.'} />
            <AB level={size.underDoseRisk} title="Under-dose Risk" message={size.underDoseRisk === 'high' ? 'Reagent dose insufficient for neutralization. Effluent pH will miss target.' : 'Dose adequate for neutralization.'} />
          </div>

          <div className="flex space-x-1 p-1 rounded-xl bg-slate-800">
            <button onClick={() => setActiveTab('influent')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'influent' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Influent</button>
            <button onClick={() => setActiveTab('tank')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'tank' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Tank / Mixer</button>
            <button onClick={() => setActiveTab('chemistry')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'chemistry' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Chemistry</button>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              {activeTab === 'influent' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Influent Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <S label="Flow Rate" value={params.flowRate} min={10} max={2000} step={10} unit="m³/d" icon={Droplets} color="#38bdf8" onChange={(v) => update('flowRate', v)} />
                    <S label="Influent pH" value={params.influentpH} min={2} max={12} step={0.1} unit="" icon={Beaker} color="#f59e0b" onChange={(v) => update('influentpH', v)} />
                    <S label="Target pH" value={params.targetpH} min={4} max={10} step={0.1} unit="" icon={CheckCircle2} color="#10b981" onChange={(v) => update('targetpH', v)} />
                    <S label="Alkalinity" value={params.influentAlkalinity} min={0} max={2000} step={10} unit="mg/L" icon={FlaskConical} color="#06b6d4" onChange={(v) => update('influentAlkalinity', v)} />
                    <S label="Influent TSS" value={params.influentTSS} min={0} max={5000} step={50} unit="mg/L" icon={Layers} color="#f43f5e" onChange={(v) => update('influentTSS', v)} />
                    <S label="Influent COD" value={params.influentCOD} min={0} max={10000} step={100} unit="mg/L" icon={Zap} color="#f43f5e" onChange={(v) => update('influentCOD', v)} />
                    <S label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v) => update('og', v)} />
                    <S label="pH (sample)" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={(v) => update('ph', v)} />
                    <S label="Alkalinity (sample)" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={FlaskConical} color="#10b981" onChange={(v) => update('alkalinity', v)} />
                    <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Layers} color="#f59e0b" onChange={(v) => update('turbidity', v)} />
                    <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v) => update('conductivity', v)} />
                    <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v) => update('hardness', v)} />
                    <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v) => update('nh3n', v)} />
                    <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v) => update('tp', v)} />
                    <S label="Temperature" value={params.temperature} min={5} max={40} step={1} unit="°C" icon={Thermometer} color="#06b6d4" onChange={(v) => update('temperature', v)} />
                    <S label="Safety Factor" value={params.safetyFactor} min={1.0} max={2.5} step={0.1} unit="" icon={ShieldAlert} color="#8b5cf6" onChange={(v) => update('safetyFactor', v)} info="Multiplier on chemical demand" />
                  </div>
                </div>
              )}
              {activeTab === 'tank' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tank & Mixer Design</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <S label="Tank Diameter" value={params.tankDiameter} min={1} max={8} step={0.1} unit="m" icon={Gauge} color="#10b981" onChange={(v) => update('tankDiameter', v)} />
                    <S label="Tank Depth" value={params.tankDepth} min={1} max={6} step={0.1} unit="m" icon={Gauge} color="#10b981" onChange={(v) => update('tankDepth', v)} />
                    <S label="Freeboard" value={params.freeboard} min={0.2} max={1.5} step={0.1} unit="m" icon={ArrowDown} color="#94a3b8" onChange={(v) => update('freeboard', v)} />
                    <S label="Mixer Power" value={params.mixerPower} min={0.1} max={10} step={0.1} unit="kW" icon={Zap} color="#f59e0b" onChange={(v) => update('mixerPower', v)} />
                    <S label="Mixer RPM" value={params.mixerRPM} min={10} max={200} step={5} unit="rpm" icon={Timer} color="#f59e0b" onChange={(v) => update('mixerRPM', v)} />
                    <S label="Impeller Diameter" value={params.mixerDiameter} min={0.2} max={2} step={0.05} unit="m" icon={CircleDot} color="#f59e0b" onChange={(v) => update('mixerDiameter', v)} />
                  </div>
                </div>
              )}
              {activeTab === 'chemistry' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Reagent Dosing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <S label="Reagent Type" value={['naoh','hcl','co2','sulfuric','lime'].indexOf(params.reagentType)} min={0} max={4} step={1} unit="" icon={FlaskConical} color="#ef4444" onChange={(v) => update('reagentType', ['naoh','hcl','co2','sulfuric','lime'][v])} />
                    <S label="Reagent Dose (user)" value={params.reagentDose} min={0} max={1000} step={5} unit="mg/L" icon={Beaker} color="#ef4444" onChange={(v) => update('reagentDose', v)} />
                    <S label="Reagent Concentration" value={params.reagentConcentration} min={1} max={98} step={1} unit="%" icon={BarChart3} color="#ef4444" onChange={(v) => update('reagentConcentration', v)} />
                    <S label="Reagent Density" value={params.reagentDensity} min={0.8} max={2.0} step={0.01} unit="kg/L" icon={Weight} color="#ef4444" onChange={(v) => update('reagentDensity', v)} />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Performance Gauges */}
          <div className="p-4 space-y-3 rb-inner-card">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><Gauge className="w-3.5 h-3.5 text-sky-400" /> Performance Gauges</h3>
            <div className="grid grid-cols-2 gap-3">
              <G value={size.actualRetention} max={120} label="Retention" unit="min" color="#06b6d4" />
              <G value={size.gValue} max={200} label="G-value" unit="s⁻¹" color="#f59e0b" />
              <G value={size.reactionCompleteness * 100} max={100} label="Reaction" unit="%" color="#10b981" />
              <G value={size.phDeviation} max={2} label="pH Deviation" unit="" color="#f43f5e" />
            </div>
          </div>

          {/* Sizing Panel */}
          <div className="p-4 space-y-2 rb-inner-card">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-emerald-400" /> Sizing & Chemistry</h3>
            <div className="space-y-1.5 text-[10px]">
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Tank Area</span><span className="text-rb-brand font-bold">{fmt(size.tankArea, 1)} m²</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Tank Volume</span><span className="text-rb-brand font-bold">{fmt(size.tankVolume, 1)} m³</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Total Volume</span><span className="text-rb-brand font-bold">{fmt(size.totalVolume, 1)} m³</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Retention</span><span className="text-rb-brand font-bold">{fmt(size.actualRetention, 0)} min</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">pH Change</span><span className="text-rb-brand font-bold">{fmt(size.pHChange, 1)}</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Alk Demand</span><span className="text-rb-brand font-bold">{fmt(size.acidBaseDemand, 0)} mg/L CaCO₃</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Reagent Demand</span><span className="text-rb-brand font-bold">{fmt(size.reagentDemand, 1)} mg/L</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Daily Consumption</span><span className="text-rb-brand font-bold">{fmt(size.dailyConsumption, 1)} kg/day</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Reagent Flow</span><span className="text-rb-brand font-bold">{fmt(size.reagentFlow, 1)} L/day</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Pump Rate</span><span className="text-rb-brand font-bold">{fmt(size.reagentPumpRate, 1)} mL/min</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Alk Shortfall</span><span className="text-rb-brand font-bold">{fmt(size.alkalinityShortfall, 0)} mg/L</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Tip Speed</span><span className="text-rb-brand font-bold">{fmt(size.tipSpeed, 1)} m/s</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">G-value</span><span className="text-rb-brand font-bold">{fmt(size.velocityGradient, 0)} s⁻¹</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Effluent pH (est)</span><span className="text-rb-brand font-bold">{fmt(size.actualEffluentPH, 1)}</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Completeness</span><span className="text-rb-brand font-bold">{fmt(size.reactionCompleteness * 100, 0)}%</span></div>
            </div>
          </div>

          {/* Design Notes */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes &amp; Warnings</h3>
            <div className="space-y-2 text-xs">
              {size.alkalinityShortfall > 50 && <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">⚠️ Alkalinity shortfall {fmt(size.alkalinityShortfall, 0)} mg/L. Influent buffering insufficient for pH target. Add external alkali.</div>}
              {size.actualRetention < 10 && <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">⚠️ Retention {fmt(size.actualRetention, 0)} min is too short for complete neutralization. Increase tank volume.</div>}
              {size.gValue < 20 && <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">ℹ️ G-value {fmt(size.gValue, 0)} s⁻¹ is low. Mixing may be insufficient. Increase mixer power or reduce volume.</div>}
              {size.phDeviation > 0.5 && <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">ℹ️ Estimated effluent pH deviation {fmt(size.phDeviation, 1)} from target. Consider adjusting dose or retention.</div>}
              <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-emerald-400">ℹ️ Camp-Stein: G = √(P/μV). Current G = {fmt(size.gValue, 0)} s⁻¹. Target 20–100 s⁻¹ for neutralization.</div>
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
